import { useRef, useState, forwardRef } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { Link } from 'react-router-dom';
import { IMAGES } from '../data/menuData';
import './FlipMenu.css';

// ── Reusable page components ─────────────────────────────────────────────────

const Cover = forwardRef(({ children, className }, ref) => (
  <div className={`flip-cover ${className || ''}`} ref={ref}>{children}</div>
));

const Page = forwardRef(({ children, number, side, photo }, ref) => (
  <div className={`flip-page-inner ${side === 'left' ? 'left-page' : ''}`} ref={ref}>
    {photo && <div className="page-photo" style={{ backgroundImage: `url(${photo})` }} />}
    <div className="page-top-rule" />
    <div className="page-brand">Gemiya</div>
    {children}
    <div className="page-number">{number}</div>
  </div>
));

// ── Menu row helpers ──────────────────────────────────────────────────────────

function Row({ name, price, desc }) {
  return (
    <>
      <div className="menu-row">
        <span className="menu-row-name">{name}</span>
        <span className="menu-row-dots" />
        {price && <span className="menu-row-price">{price}</span>}
      </div>
      {desc && <div className="menu-row-desc">{desc}</div>}
    </>
  );
}

function Section({ title, sub, children }) {
  return (
    <>
      <div className="page-section-title">{title}</div>
      {sub && <div className="page-section-sub">{sub}</div>}
      {children}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function FlipMenu() {
  const bookRef = useRef();
  const [page, setPage] = useState(0);
  const TOTAL = 10; // total pages (cover + inner + back)

  const prev = () => bookRef.current?.pageFlip().flipPrev();
  const next = () => bookRef.current?.pageFlip().flipNext();

  return (
    <div className="flipmenu-root">
      <div className="flipmenu-header">
        <Link to="/menu" className="flipmenu-back">← Back to Menu</Link>
        <span className="flipmenu-hint">Click edges or swipe to turn pages</span>
      </div>

      <div className="flipmenu-book-wrap">
        <button className="flip-arrow flip-arrow-prev" onClick={prev} disabled={page === 0} aria-label="Previous page">‹</button>

        <HTMLFlipBook
          ref={bookRef}
          width={420}
          height={600}
          size="stretch"
          minWidth={280}
          maxWidth={520}
          minHeight={400}
          maxHeight={720}
          maxShadowOpacity={0.5}
          showCover={true}
          mobileScrollSupport={false}
          onFlip={(e) => setPage(e.data)}
          className="flip-book"
        >

          {/* ── PAGE 0: FRONT COVER ── */}
          <Cover>
            <img src="/gemiya-logo.png" alt="Gemiya" className="cover-logo" />
            <h1 className="cover-title">MENU</h1>
            <p className="cover-subtitle">Authentique Sri Lanka Cuisine</p>
            <div className="cover-divider" />
            <p className="cover-tagline">
              "Les saveurs authentiques<br />de l'île aux épices"
            </p>
          </Cover>

          {/* ── PAGE 1: Sri Lankan Buffet & Salads ── */}
          <Page number={1} photo={IMAGES.curry}>
            <Section title="Sri Lankan Buffet">
              <Row name="Sri Lankan Buffet" price="13 €" desc="Riz vapeur, currys du jour, sambols maison · Déjeuner uniquement" />
            </Section>
            <div className="page-rule" />
            <Section title="Salads">
              <Row name="Beetroot Mixed Salad"      price="8 €"  desc="Beetroot, cucumber, dill onion, green chilli, lemon" />
              <Row name="Chicken Caesar Salad"      price="8 €"  />
              <Row name="Sri Lankan Gotokola Salad" price="8 €"  desc="Bal sauce, dry fish, lemon juice, coconut" />
              <Row name="Karunella Salad"           price="12 €" desc="Onions, dry fish, lemon juice, green chilli" />
            </Section>
            <div className="page-rule" />
            <Section title="Hoppers" sub="Appam · crêpes de riz fermenté">
              <Row name="Egg Hopper"     price="3 €" />
              <Row name="Milk Hopper"    price="3 €" />
              <Row name="Cheese Hopper"  price="3 €" />
              <Row name="Sausage Hopper" price="3 €" />
            </Section>
            <div className="page-rule" />
            <Section title="Pittu" sub="Served with coconut milk curry">
              <Row name="Chicken Curry Pittu" price="12 €" />
              <Row name="Fish Curry Pittu"    price="12 €" />
            </Section>
          </Page>

          {/* ── PAGE 2: Fried Rice ── */}
          <Page number={2} side="left" photo={IMAGES.curry}>
            <Section title="Fried Rice" sub="Rice & Fried Rice · Served with sambol">
              <Row name="Veg Fried Rice"     price="10 €" />
              <Row name="Egg Fried Rice"     price="12 €" />
              <Row name="Chicken Fried Rice" price="13 €" />
              <Row name="Beef Fried Rice"    price="14 €" />
              <Row name="Mutton Fried Rice"  price="14 €" />
              <Row name="Seafood Fried Rice" price="15 €" />
              <Row name="Nasi Goreng"        price="15 €" />
              <Row name="Mixed Fried Rice"   price="15 €" />
            </Section>
          </Page>

          {/* ── PAGE 3: Kottu ── */}
          <Page number={3} photo={IMAGES.kottu}>
            <Section title="Kottu Roti" sub="Le plat national sri lankais · roti haché, légumes, épices">
              <Row name="Veg Kottu"      price="10 €" />
              <Row name="Egg Kottu"      price="12 €" />
              <Row name="Chicken Kottu"  price="13 €" />
              <Row name="Fish Kottu"     price="14 €" />
              <Row name="Beef Kottu"     price="14 €" />
              <Row name="Mutton Kottu"   price="15 €" />
              <Row name="Seafood Kottu"  price="15 €" />
              <Row name="Dolphin Kottu"  price="15 €" />
            </Section>
          </Page>

          {/* ── PAGE 4: Devilled Dishes ── */}
          <Page number={4} side="left" photo={IMAGES.pork}>
            <Section title="Devilled Dishes" sub="Spicy Sri Lankan Stir Fry · épicé, savoureux">
              <Row name="Devilled Chicken"     price="13 €" />
              <Row name="Devilled Fish"        price="14 €" />
              <Row name="Devilled Seafood"     price="14 €" />
              <Row name="Devilled Prawn"       price="14 €" />
              <Row name="Devilled Garlic Beef" price="14 €" />
              <Row name="Roasted Pork"         price="14 €" />
            </Section>
            <div className="page-rule" />
            <Section title="Chapsuey" sub="Hot Pot · Beef, Mutton, Chicken ou Seafood">
              <Row name="Mix – Beef + Mutton + Chicken" price="16 €" />
              <Row name="Mix – Sea Food"                price="16 €" />
              <Row name="Veg Chapsuey (with Rice)"     price="13 €" />
              <Row name="Chicken Chapsuey (with Rice)" price="14 €" />
              <Row name="Beef Chapsuey (with Rice)"    price="14 €" />
            </Section>
          </Page>

          {/* ── PAGE 5: Pizza & Burgers ── */}
          <Page number={5} photo={IMAGES.hoppers}>
            <Section title="Burgers" sub="Served with fries">
              <Row name="Cheeseburger" price="12 €" />
            </Section>
            <div className="page-rule" />
            <Section title="Pizza">
              <Row name="Pizza Gemiya"     price="12 €" desc="Cheese, peppers, red sauce, chicken, beef, mutton, curry leaves" />
              <Row name="Pizza Margherita" price="10 €" desc="Cheese, oregano" />
            </Section>
            <div className="page-rule" />
            <Section title="Drinks">
              <Row name="Mango Juice"        price="4 €"    />
              <Row name="Passion Fruit Juice" price="4 €"   />
              <Row name="Soft Drinks"        price="2.50 €" />
              <Row name="Iced Tea (Lemon)"   price="3.50 €" />
            </Section>
            <div className="page-rule" />
            <Section title="Desserts">
              <Row name="Wadappan"          price="4 €" />
              <Row name="Coconut Pudding"   price="4 €" />
              <Row name="Ice Cream (Vanilla)" price="4 €" />
            </Section>
          </Page>

          {/* ── PAGE 6–7: Blank inner pages (spine area, kept minimal) ── */}
          <Page number={6} side="left">
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', opacity: .35 }}>
              <img src="/gemiya-logo.png" alt="" style={{ width: 80, opacity: .6 }} />
              <p style={{ fontFamily: "'Cinzel', serif", fontSize: '.58rem', letterSpacing: '.25em', color: '#B5611A', textTransform: 'uppercase' }}>
                Authentique<br />Sri Lanka Cuisine
              </p>
            </div>
          </Page>

          <Page number={7}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '.5rem' }}>
              <div className="page-section-title">Réservations</div>
              <p style={{ fontSize: '.82rem', color: '#8B7355', lineHeight: 1.7, fontStyle: 'italic' }}>
                Réservez votre table en ligne ou par téléphone. Nous vous accueillons avec plaisir chaque jour.
              </p>
              <div style={{ marginTop: '1rem' }}>
                <Row name="Téléphone" price="+33 7 88 31 30 74" />
                <Row name="Email"     price="bonjour@gemiya.fr" />
              </div>
              <div className="page-rule" />
              <div className="page-section-title">Horaires</div>
              <Row name="Lun – Ven" price="11h30 – 22h30" />
              <Row name="Samedi"    price="11h30 – 23h00" />
              <Row name="Dimanche"  price="12h00 – 22h00" />
            </div>
          </Page>

          {/* ── PAGE 8 (last inner) — allergen / note ── */}
          <Page number={8} side="left">
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '.7rem' }}>
              <div className="page-section-title">À noter</div>
              <p style={{ fontSize: '.78rem', color: '#8B7355', lineHeight: 1.75, fontStyle: 'italic' }}>
                Tous nos plats sont préparés à la commande avec des ingrédients frais. Nous pouvons adapter les plats aux allergies et régimes alimentaires — n'hésitez pas à nous le signaler.
              </p>
              <p style={{ fontSize: '.78rem', color: '#8B7355', lineHeight: 1.75, fontStyle: 'italic' }}>
                All our dishes are freshly prepared to order. Please inform us of any allergies or dietary requirements.
              </p>
              <div className="page-rule" />
              <p style={{ fontSize: '.7rem', color: 'rgba(181,97,26,.4)', textAlign: 'center', letterSpacing: '.05em' }}>
                Les prix sont indiqués en euros toutes taxes comprises.<br />
                Prices are inclusive of all taxes.
              </p>
            </div>
          </Page>

          {/* ── PAGE 9 (BACK COVER) ── */}
          <Cover>
            <img src="/gemiya-logo.png" alt="Gemiya" className="cover-logo" />
            <h2 style={{ fontFamily: "'Cinzel', serif", fontSize: '1.1rem', color: '#D4873A', letterSpacing: '.2em', fontWeight: 400, margin: '0 0 .3rem' }}>GEMIYA</h2>
            <p style={{ fontSize: '.7rem', color: 'rgba(242,230,204,.4)', letterSpacing: '.15em', textTransform: 'uppercase', margin: '0 0 1.2rem' }}>Authentique Sri Lanka Cuisine</p>
            <div className="cover-divider" />
            <div className="cover-contact">
              <div><a href="tel:+33788313074">+33 7 88 31 30 74</a></div>
              <div><a href="mailto:bonjour@gemiya.fr">bonjour@gemiya.fr</a></div>
              <div>France</div>
            </div>
            <div className="cover-hours-title">Horaires d'ouverture</div>
            <div className="cover-hours-row"><span>Lun – Ven</span><span>11h30 – 22h30</span></div>
            <div className="cover-hours-row"><span>Samedi</span><span>11h30 – 23h00</span></div>
            <div className="cover-hours-row"><span>Dimanche</span><span>12h00 – 22h00</span></div>
          </Cover>

        </HTMLFlipBook>

        <button className="flip-arrow flip-arrow-next" onClick={next} disabled={page >= TOTAL - 1} aria-label="Next page">›</button>
      </div>

      <div className="flipmenu-counter">
        Page {page + 1} of {TOTAL}
      </div>
    </div>
  );
}
